import { getAllTempMessages, updateTempMessage } from "../config/db";
import { msgEncrypter } from "../utils/messageEncrypt";
import { uploadFileCache } from "../utils/helper";
import { MessageType } from "../utils/constant";
import {
  handleTaskUpdate,
  uploadAttachment,
} from "../appStore/actions/ArkchatGroup/action";
import { showToastMsg } from "../appStore/actions/global/action";

// Resending messages that didn't go out (offline, connection down, send
// failed). When a message is sent, its pending copy records an `outbox`:
//   { channel, message, files }                    a chat message to publish
//   { kind: "task-update", taskId, body, files }   a task/approval update (PUT)
// `files` are attachments not uploaded yet, kept in this tab's memory
// (uploadFileCache). Resending replays exactly that send. The live event
// (or the task update's reply) then replaces the pending copy; saving is
// idempotent on temp_timetoken, so a repeat send can't duplicate a message.

const RESEND_GAP_MS = 350; // stays under the gateway's 30 sends / 10 s limit
const inFlight = new Set();
let resendingAll = false;

const encrypt = (text, kuncika) =>
  msgEncrypter.encrypt(text, process.env.REACT_APP_ENC_KEY, kuncika);

// Pending copies saved before outboxes existed: rebuild plain text sends the
// way the send code built them. Anything else can't be replayed safely.
function legacyOutbox(pending, userId) {
  const plain = pending?.type === MessageType.Message || pending?.type === MessageType.Direct_Mesage;
  if (!plain || pending.id || pending.chain || pending.fileData?.length) return null;
  const { fileData, outbox, ...body } = pending;
  body.message = encrypt(pending.message, pending.kuncika);
  if (pending.thread_original_message) {
    body.thread_original_message = {
      ...pending.thread_original_message,
      message: encrypt(pending.thread_original_message.message, pending.kuncika),
      translations: encrypt(
        JSON.stringify(pending.thread_original_message.translations),
        pending.kuncika,
      ),
    };
  }
  if (pending.type === MessageType.Direct_Mesage) {
    return {
      channel: pending.pnId,
      message: { type: "message", content: { message: body, type: "direct-message" } },
    };
  }
  const isPrivate = !pending.is_all_group_member;
  const channel = isPrivate ? `${pending.pnId}.user_${userId}` : pending.pnId;
  const isReply = !!pending.thread_id;
  return {
    channel,
    message: {
      type: "message",
      content: {
        message: { ...body, is_private: isPrivate, ...(isReply && { is_main: false }) },
        type: isReply ? "message-thread" : "conversation-message",
      },
      is_private: isPrivate,
    },
  };
}

// Uploads the outbox's files; returns their uploaded records, or null if the
// files are gone (the page was reloaded) and must be attached again.
async function uploadFiles(outbox, pnId, dispatch) {
  const refs = outbox.files || [];
  if (refs.length === 0) return [];
  const files = refs.map((f) => uploadFileCache.get(f.id)).filter(Boolean);
  if (files.length < refs.length) return null;
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));
  const uploaded = await dispatch(uploadAttachment(formData, pnId));
  if (!Array.isArray(uploaded) || uploaded.length === 0) {
    throw new Error("upload_failed");
  }
  refs.forEach((f) => uploadFileCache.delete(f.id));
  return uploaded;
}

async function send(pending, { pubnub, dispatch, userId }) {
  const outbox = pending.outbox || legacyOutbox(pending, userId);
  if (!outbox) return "unsupported";
  const uploaded = await uploadFiles(outbox, pending.pnId, dispatch);
  if (uploaded === null) return "files_missing";

  if (outbox.kind === "task-update") {
    const body = uploaded.length > 0
      ? { ...outbox.body, attachments: [...(outbox.body.attachments || []), ...uploaded] }
      : outbox.body;
    if (uploaded.length > 0) {
      await updateTempMessage(pending.temp_timetoken, { outbox: { ...outbox, body, files: [] } });
    }
    const response = await dispatch(handleTaskUpdate(body, outbox.taskId));
    if (response?.status !== 200) throw new Error("task_update_failed");
    // The update's live event refreshes what's on screen (see useTabState).
    return "sent";
  }

  let message = outbox.message;
  if (uploaded.length > 0) {
    const inner = message.content.message;
    message = {
      ...message,
      content: {
        ...message.content,
        message: { ...inner, attachments: [...(inner.attachments || []), ...uploaded] },
      },
    };
    // Keep the uploaded files so a later retry doesn't upload them again.
    await updateTempMessage(pending.temp_timetoken, { outbox: { ...outbox, message, files: [] } });
  }
  await pubnub.publish({ channel: outbox.channel, message });
  return "sent";
}

const CONNECTIVITY_ERRORS = ["offline", "realtime_send_timeout"];

// One attempt: "sent", "busy" (already being sent), "unsupported",
// "files_missing", "offline" (no connection; retried on reconnect) or
// "failed" (rejected/errored while connected).
async function attempt(pending, ctx) {
  const key = pending?.temp_timetoken;
  if (!key || !ctx?.pubnub) return "unsupported";
  if (inFlight.has(key)) return "busy";
  inFlight.add(key);
  try {
    return await send(pending, ctx);
  } catch (error) {
    const reason = error?.status?.category || error?.message;
    return !navigator.onLine || CONNECTIVITY_ERRORS.includes(reason) ? "offline" : "failed";
  } finally {
    inFlight.delete(key);
  }
}

// The Resend button: resend one message and say what went wrong, if anything.
export async function resendPending(pending, ctx) {
  const result = await attempt(pending, ctx);
  const messages = {
    files_missing: "The attachment is no longer available. Please delete this message and attach it again.",
    unsupported: "This message can't be resent. Please delete it and send it again.",
    offline: "You're offline. The message will be sent when you're back online.",
    failed: "Couldn't send the message. Please try again.",
  };
  if (messages[result]) ctx.dispatch(showToastMsg(messages[result]));
  return result === "sent";
}

// Resend everything still pending, oldest first, when the connection comes
// back. Stops when the connection is gone again (so later messages don't
// overtake earlier ones); skips messages that can't be sent automatically or
// that the server rejected (they keep their Resend button).
export async function resendAllPending(ctx) {
  if (resendingAll || !ctx?.pubnub) return;
  resendingAll = true;
  try {
    const pending = await getAllTempMessages();
    for (const msg of pending) {
      if (!navigator.onLine) break;
      const result = await attempt(msg, ctx);
      if (result === "offline") break;
      if (result === "sent") await new Promise((resolve) => setTimeout(resolve, RESEND_GAP_MS));
    }
  } finally {
    resendingAll = false;
  }
}
